import useFormatter from "@/hooks/useFormatter";
import { calcSilverWeight } from "@/lib/transactionCalculations";
import { statsKeys } from "@/query-keys";
import { services } from "@/services";
import type { ClientBalance } from "@/schemas/clientSchema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import useStatsQuery from "./stats/_hooks/useStatsQuery";
import {
  UserPlus,
  ShieldAlert,
  AlertTriangle,
  FileSearch,
  RefreshCw,
} from "lucide-react";
import { StatGridSkeleton } from "@/components/loading";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import StatsSummary from "./stats/_components/StatsSummary";

export const Route = createFileRoute("/_private/stats")({
  loader: async ({ context: { queryClient } }) => {
    const stats = await queryClient.ensureQueryData({
      queryKey: statsKeys.clientsOverview(),
      queryFn: () => services.getClientsStats(),
    });
    return { stats };
  },
  component: StatsPage,
});

function ClientTable({
  items,
  emptyMessage,
  t,
  formatter,
}: {
  items: ClientBalance[];
  emptyMessage: string;
  t: ReturnType<typeof useTranslation>["t"];
  formatter: ReturnType<typeof useFormatter>;
}) {
  return (
    <div className="rounded-xl border overflow-hidden">
      <ScrollArea className="max-h-110 w-full">
        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b bg-muted sticky top-0 z-10 transition-none">
              <TableHead>{t("stats.table.client")}</TableHead>
              <TableHead className="text-right">{t("stats.table.goldBalance")}</TableHead>
              <TableHead className="text-right">{t("stats.table.cashBalance")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <FileSearch className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              items.map((c) => (
                <TableRow key={c.clientId} className="hover:bg-muted/10">
                  <TableCell className="font-semibold capitalize">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: c.clientId }}
                      className="hover:underline"
                    >
                      {c.clientName}
                    </Link>
                  </TableCell>
                  <TableCell
                    className={`text-right font-medium tabular-nums ${c.goldBalance < 0 ? "text-red-600" : "text-emerald-600"}`}
                  >
                      <span>
                          <span>{formatter.weight(Math.abs(c.goldBalance), 3)}</span>
                          <br />
                          <span className="text-xs text-muted-foreground font-normal">
                            {t("stats.summary.fields.inboundPureWeight")}: {formatter.weight(c.inboundPureWeight, 3)} • {t("transactions.form.silverWeight")}: {formatter.weight(calcSilverWeight(c.inboundPureWeight), 3)}
                          </span>
                        </span>
                  </TableCell>
                  <TableCell
                    className={`text-right font-medium tabular-nums ${c.cashBalance < 0 ? "text-red-600" : "text-emerald-600"}`}
                  >
                    {formatter.currency(Math.abs(c.cashBalance))}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

function StatsPage() {
  const { t } = useTranslation();
  const formatter = useFormatter();
  const { stats: initialStats } = Route.useLoaderData();
  const { data, isFetching, isError, error, refetch } = useStatsQuery(initialStats);

  if (isError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">{error?.message}</p>
        <button onClick={() => refetch()} className="text-sm text-primary hover:underline">
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1 flex flex-col gap-8 pb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("stats.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("stats.subtitle")}</p>
        </div>
        <div className="space-y-4">
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <StatGridSkeleton cols={4} />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <StatGridSkeleton cols={4} />
          </div>
        </div>
        {[1, 2, 3].map((i) => (
          <section key={i} className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-8 pb-8">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{t("stats.title")}</h1>
          {isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <p className="text-muted-foreground text-sm mt-1">{t("stats.subtitle")}</p>
      </div>

      <StatsSummary />

      <section className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("stats.debt.title")}
          </h2>
        </div>
        <ClientTable
          items={data?.clientsWithDebt ?? []}
          emptyMessage={t("stats.debt.empty")}
          t={t}
          formatter={formatter}
        />
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("stats.newClients.title")}
          </h2>
        </div>
        <ClientTable
          items={data?.newClientsThisMonth ?? []}
          emptyMessage={t("stats.newClients.empty")}
          t={t}
          formatter={formatter}
        />
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <ShieldAlert className="h-4 w-4 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("stats.flaggedClients.title")}
          </h2>
        </div>
        <ClientTable
          items={data?.flaggedClients ?? []}
          emptyMessage={t("stats.flaggedClients.empty")}
          t={t}
          formatter={formatter}
        />
      </section>
    </div>
  );
}
